import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams, Link, useLocation } from 'react-router-dom';
import { Mail, Lock, User, AlertCircle, LogIn, UserPlus, Scale } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { useLanguage } from '../../contexts/LanguageContext';
import PageTitle from '../../components/common/PageTitle';
import { ROUTES } from '../../constants/routes';
import { supabase } from '../../lib/supabase';

const AuthPage: React.FC = () => {
  // Add the language hook
  const { t, language } = useLanguage();
  
  // State management
  const [isLogin, setIsLogin] = useState(true);
  const [searchParams] = useSearchParams();
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [googleLoading, setGoogleLoading] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const { signIn, signUp } = useAuth();
  
  // Check if user was redirected after email verification
  const verified = searchParams.get('verified') === 'true';
  
  useEffect(() => {
    // Set page title based on mode
    document.title = isLogin ? `${t('sign_in')} | Legal Nest` : `${t('create_account')} | Legal Nest`;
    
    // Clear any error messages when switching modes
    setErrorMessage('');
    setSuccessMessage('');
  }, [isLogin, t]);
  
  // Handle form submission for both login and registration
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!email || !password) {
      setErrorMessage(t('please_fill_all_fields'));
      return;
    }
    
    try {
      setIsLoading(true);
      setErrorMessage('');
      
      // Always redirect to home page after login
      const returnTo = ROUTES.HOME;
      
      if (isLogin) {
        await signIn(email, password, returnTo);
      } else {
        const result = await signUp(email, password, { name }, returnTo);
        
        if (result.needsEmailVerification) {
          setSuccessMessage(t('verification_email_sent'));
          setTimeout(() => {
            navigate(`/verify-email?email=${encodeURIComponent(email)}`);
          }, 2000);
        }
      }
    } catch (error: any) {
      console.error("Auth error:", error);
      setErrorMessage(error.message || (isLogin ? t('login_failed') : t('registration_failed')));
    } finally {
      setIsLoading(false);
    }
  };
  
  // Handle Google sign-in
  const handleGoogleSignIn = async () => {
    try {
      setGoogleLoading(true);
      setErrorMessage('');
      
      // Create the redirect URL based on the environment
      const baseUrl = window.location.origin;
      const isLocalDevelopment = baseUrl.includes('localhost') || 
        baseUrl.includes('127.0.0.1') || 
        baseUrl.match(/\d+\.\d+\.\d+\.\d+/);
      
      let redirectUrl = `${window.location.origin}/auth/callback`;
      
      // For production, ensure we're using https and no hash fragment
      if (!isLocalDevelopment) {
        redirectUrl = redirectUrl.replace('http://', 'https://');
      }
      
      console.log("Setting OAuth redirect URL:", redirectUrl);
      
      const { data, error } = await supabase.auth.signInWithOAuth({
        provider: 'google',
        options: {
          redirectTo: redirectUrl,
          queryParams: {
            access_type: 'offline',
            prompt: 'consent'
          }
        }
      });
      
      if (error) throw error;
      
      // The redirect will happen automatically from Supabase
      console.log('Google sign-in initiated:', data);
      
    } catch (error: any) {
      console.error("Google sign-in error:", error);
      setErrorMessage(error.message || 'Failed to sign in with Google');
      setGoogleLoading(false);
    }
  };

  return (
    <div className="container-custom py-12">
      <PageTitle title={isLogin ? t('sign_in') : t('create_account')} />
      
      <div className="max-w-md mx-auto bg-white rounded-lg shadow-md p-8">
        {/* Success or verification message */}
        {(verified || successMessage) && (
          <div className="mb-6 p-4 bg-green-50 text-green-700 rounded-md border border-green-100">
            {verified ? 'Your email has been verified! You can now sign in.' : successMessage}
          </div>
        )}
        
        {/* Error message */}
        {errorMessage && (
          <div className="mb-6 p-4 bg-red-50 text-red-700 rounded-md border border-red-100 flex items-start">
            <AlertCircle size={20} className="mr-2 flex-shrink-0 mt-0.5" />
            <div>{errorMessage}</div>
          </div>
        )}
        
        {/* Legal Nest Logo */}
        <div className="flex justify-center mb-6">
          <div className="flex flex-col items-center">
            <div className="flex items-center">
              <Scale size={32} className="text-primary-500 mr-2" />
              <span className="font-serif font-bold text-2xl text-primary-500">Legal Nest</span>
            </div>
          </div>
        </div>
        
        {/* Google Sign-In Button */}
        <div className="mb-6">
          <button 
            onClick={handleGoogleSignIn}
            className="w-full flex items-center justify-center bg-white border border-neutral-300 rounded-md py-3 px-4 text-neutral-700 hover:bg-neutral-50 transition-colors duration-300"
            disabled={googleLoading || isLoading}
          >
            {googleLoading ? (
              <div className="animate-spin h-5 w-5 border-2 border-neutral-400 border-t-transparent rounded-full mr-2"></div>
            ) : (
              <svg className="w-5 h-5 mr-2" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4" />
                <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853" />
                <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05" />
                <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335" />
              </svg>
            )}
            {isLogin ? t('login_with_google') : t('signup_with_google')}
          </button>
        </div>
        
        {/* Separator */}
        <div className="flex items-center mb-6">
          <div className="flex-1 border-b border-neutral-200"></div>
          <span className="mx-4 text-neutral-500 text-sm">{t('or')}</span>
          <div className="flex-1 border-b border-neutral-200"></div>
        </div>
        
        {/* Email/Password Form */}
        <form onSubmit={handleSubmit}>
          {!isLogin && (
            <div className="mb-4">
              <label htmlFor="name" className="block mb-1 text-sm font-medium">
                {t('full_name')}
              </label>
              <div className="relative">
                <div className="absolute left-3 top-3 text-neutral-400">
                  <User size={18} />
                </div>
                <input 
                  type="text"
                  id="name"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  className="form-input w-full pl-10 py-2.5 border border-neutral-300 rounded-md"
                  placeholder={language === 'hi' ? 'पूरा नाम' : 'John Doe'}
                  required={!isLogin}
                  autoFocus={!isLogin}
                />
              </div>
            </div>
          )}
          
          {/* Email field */}
          <div className="mb-4">
            <label htmlFor="email" className="block mb-1 text-sm font-medium">
              {t('email_address')}
            </label>
            <div className="relative">
              <div className="absolute left-3 top-3 text-neutral-400">
                <Mail size={18} />
              </div>
              <input 
                type="email"
                id="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="form-input w-full pl-10 py-2.5 border border-neutral-300 rounded-md"
                placeholder="your.email@example.com"
                required
                autoFocus={isLogin}
              />
            </div>
          </div>
          
          {/* Password field */}
          <div className="mb-6">
            <div className="flex justify-between items-center mb-1">
              <label htmlFor="password" className="text-sm font-medium">
                {t('password')}
              </label>
              {isLogin && (
                <Link to="/forgot-password" className="text-xs text-primary-600 hover:underline">
                  {t('forgot_password')}
                </Link>
              )}
            </div>
            <div className="relative">
              <div className="absolute left-3 top-3 text-neutral-400">
                <Lock size={18} />
              </div>
              <input 
                type="password"
                id="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="form-input w-full pl-10 py-2.5 border border-neutral-300 rounded-md"
                placeholder="••••••••"
                required
                minLength={isLogin ? undefined : 6}
              />
            </div>
            {!isLogin && (
              <p className="text-xs text-neutral-500 mt-1">
                {t('password_requirement')}
              </p>
            )}
          </div>
          
          {/* Submit button */}
          <button 
            type="submit"
            className="btn btn-primary w-full flex items-center justify-center"
            disabled={isLoading}
          >
            {isLoading ? (
              <>
                <div className="animate-spin h-4 w-4 mr-2 border-2 border-white border-t-transparent rounded-full"></div>
                {isLogin ? t('signing_in') : t('creating_account')}
              </>
            ) : (
              <>
                {isLogin ? (
                  <>
                    <LogIn size={18} className="mr-2" />
                    {t('sign_in')}
                  </>
                ) : (
                  <>
                    <UserPlus size={18} className="mr-2" />
                    {t('create_account')}
                  </>
                )}
              </>
            )}
          </button>
        </form>
        
        {/* Toggle between login and register */}
        <p className="text-center mt-6 text-neutral-600">
          {isLogin ? t('dont_have_account') : t('already_have_account')}{' '}
          <button 
            onClick={() => setIsLogin(!isLogin)}
            className="text-primary-600 hover:underline"
          >
            {isLogin ? t('create_new_account') : t('sign_in_instead')}
          </button>
        </p>
        
        {/* Terms acceptance notice */}
        <p className="text-center mt-8 text-xs text-neutral-500">
          {t('by_continuing')}{' '}
          <Link to="/terms" className="text-primary-600 hover:underline">{t('terms_of_use')}</Link>{' '}
          {t('and')}{' '}
          <Link to="/privacy" className="text-primary-600 hover:underline">{t('privacy_policy')}</Link>.
        </p>
      </div>
    </div>
  );
};

export default AuthPage;